import { useMemo } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { faUser } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles } from "tss-react/mui"

import { AttendeeDetails } from "../../createUpdateBookingDialog/attendees/attendeesForm/attendeesTypes"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { tailwindPalette } from "../../../../../modules/theme/tailwindPalette"

import { AccordionSection } from "./AccordionSection"

const useStyles = makeStyles()((theme) => ({
    icon: {
        color: tailwindPalette.slate[400],
        marginTop: ".25rem",
    },
    accordion: {
        background: tailwindPalette.emerald[50],
        [theme.breakpoints.down("sm")]: {
            background: "white",
            borderRadius: 0,
            borderBottom: `1px solid ${theme.palette.divider}`,
            "&  .MuiAccordionSummary-root": {
                padding: "0 1.25rem 2rem 1.25rem",
            },
        },
        "@media print": {
            height: "fit-content",
        },
    },
    textColor: {
        color: theme.palette.text.primary,
    },
    container: {
        display: "grid",
        gap: "1rem",
        gridTemplateColumns: "1fr 1fr",
    },
    subtitleContainer: {
        "@media print": {
            position: "absolute",
            top: "1rem",
            right: "1.5rem",
        },
    },
    accordionDetails: {
        "@media print": {
            display: "none",
        },
    },
}))

type AttendeesProps = Readonly<{
    list: AttendeeDetails[]
}>

type AttendeesCardProps = Readonly<{
    attendees: AttendeeDetails[]
    title: string
}>

function AttendeesCard({ attendees, title }: AttendeesCardProps) {
    const formatName = (a: AttendeeDetails) => {
        const company = a.company.trim()
        return company ? `${a.name} (${company})` : a.name
    }

    return (
        <Stack gap=".5rem">
            <Typography variant="subtitle1">{title}</Typography>
            <Typography variant="body1">{attendees.map((person) => formatName(person)).join(", ")}</Typography>
        </Stack>
    )
}

export function Attendees({ list }: AttendeesProps) {
    const { classes } = useStyles()

    const { internalAttendees, externalAttendees } = useMemo(() => {
        const internal: AttendeeDetails[] = []
        const external: AttendeeDetails[] = []

        for (const attendee of list) {
            if (attendee.isExternal) {
                external.push(attendee)
            } else {
                internal.push(attendee)
            }
        }

        return { internalAttendees: internal, externalAttendees: external }
    }, [list])

    const internalCount = internalAttendees.length
    const externalCount = externalAttendees.length

    return (
        <LocalizedStrict id="confirm-order-dialog-attendees" attrs={{ title: true }}>
            <AccordionSection
                title="Participants"
                subTitle={
                    <Stack flexDirection="row" gap=".5rem" className={classes.subtitleContainer}>
                        <FontAwesomeIcon icon={faUser} className={classes.icon} />
                        <LocalizedStrict
                            id="confirm-order-dialog-attendees-subtitle"
                            vars={{
                                internalCount: internalCount,
                                externalCount: externalCount,
                            }}
                        >
                            <Typography variant="body1" className={classes.textColor}>
                                {internalCount} internal, {externalCount} external
                            </Typography>
                        </LocalizedStrict>
                    </Stack>
                }
                updateAccordionUi={classes.accordion}
                updateAccordionDetails={classes.accordionDetails}
            >
                <Box className={classes.container}>
                    {!!internalCount && (
                        <LocalizedStrict id="confirm-order-dialog-attendees-internal" attrs={{ title: true }}>
                            <AttendeesCard attendees={internalAttendees} title="Internal attendees:" />
                        </LocalizedStrict>
                    )}
                    {!!externalCount && (
                        <LocalizedStrict id="confirm-order-dialog-attendees-external" attrs={{ title: true }}>
                            <AttendeesCard attendees={externalAttendees} title="External attendees:" />
                        </LocalizedStrict>
                    )}
                </Box>
            </AccordionSection>
        </LocalizedStrict>
    )
}
